import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import "./FeedCardMediaPreviewVideo.scss";
import { apiCropImage } from '../../../../config';
import { useVideoPlay } from '../VideoPlayProvider';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faVolumeOff, faVolumeUp, faPlay} from "@fortawesome/free-solid-svg-icons";
import ReactPlayer from 'react-player';

const FeedCardMediaPreviewVideo = ({
    playing = false,
    preload = false,
    width,
    height,
    media: { _id, title, videoCover, previewUrl, videoUrlFormat },
    handleClick
}) => {
    const [isMuted, setMuted] = useState(true);
    const [isBuffering, setBuffering] = useState(true);
    const [isPlayingLocally, setPlayingLocally] = useState(true);
    const [isVideoReady, setVideoReady] = useState(false);

    const handleVideoReady = useCallback(() => setVideoReady(true), []);
    const handleBuffer = useCallback(() => setBuffering(true), []);
    const handleBufferEnd = useCallback(() => setBuffering(false), []);
    const handlePlay = useCallback(() => setPlayingLocally(true), []);
    const handleError = useCallback((e) => {
        console.error("Video Player Error:", e);
        setPlayingLocally(false);
    }, []);
    const toggleMute = useCallback(() => setMuted(prevMuted => !prevMuted), []);

    const previewUri = videoCover || (Array.isArray(previewUrl) ? previewUrl[0] : previewUrl);
    const videoPreviewUri = apiCropImage({ url: previewUri, width, quality: 70 });

    return (
        <Fragment>
            <img src={videoPreviewUri} alt={title} style={{ minHeight: `${width}px`, maxHeight: `${height}px` }} />
            
            {!isPlayingLocally && (
                <div className="react-player-play">
                    <div className="react-player-play-button" onClick={handlePlay}>
                        <FontAwesomeIcon icon={faPlay} />
                    </div>
                </div>
            )}

            {(playing && isBuffering) && (
                <div className="react-player-buffering">
                    <div className="react-player-buffering-animate"></div>
                </div>
            )}

            <div className="react-player-volume" onClick={toggleMute}>
                {isMuted ? <FontAwesomeIcon icon={faVolumeOff} /> : <FontAwesomeIcon icon={faVolumeUp} />}
            </div>

            {preload && (
                <ReactPlayer
                    onClick={handleClick}
                    key={_id}
                    id={`player-${_id || "error"}`}
                    playing={playing && isPlayingLocally}
                    muted={isMuted}
                    url={videoUrlFormat?.hd}
                    className="react-player"
                    width={width}
                    height="100%"
                    loop
                    controls={false}
                    playsinline
                    style={{ opacity: isVideoReady ? 1 : 0 }}
                    onReady={handleVideoReady}
                    onBuffer={handleBuffer}
                    onBufferEnd={handleBufferEnd}
                    onPlay={handlePlay}
                    onError={handleError}
                />
            )}
        </Fragment>
    );
};

const MomoizedFeedCardMediaPreviewVideo = React.memo(FeedCardMediaPreviewVideo, (prevProps, nextProps) => {
    return prevProps?.media?._id === nextProps?.media?._id &&
           prevProps.playing === nextProps.playing &&
           prevProps.preload === nextProps.preload &&
           prevProps.media === nextProps.media &&
           prevProps.height === nextProps.height;
});

const FeedCardMediaPreviewVideoHOC = ({media, parentWidth = 400, modalIsOpen= false, handleClick=()=>{}}) => {
    const [videoState, setVideoState] = useState({playing:false, preload:false});
    const { playingVideoId, registerVideoInView, unregisterVideo } = useVideoPlay();
    const [ref, inView, entry] = useInView({
        triggerOnce: false,
        threshold: [0, 0.25, 0.5, 0.75, 1]
    });

    useEffect(() => {
        if (inView) {
            registerVideoInView(media._id, entry?.intersectionRatio);
        } else if (!inView) {
            unregisterVideo(media._id);
        }
        return ()=>{
            unregisterVideo(media._id)
        }
    }, [inView, media._id, registerVideoInView, unregisterVideo, entry?.intersectionRatio]);

    useEffect(() => {
        const playing = playingVideoId === media._id && inView && !modalIsOpen;
        if(videoState.playing !== playing || videoState.preload !== inView) setVideoState({playing, preload:inView})
    }, [playingVideoId, inView, videoState.playing, videoState.preload, media._id, modalIsOpen]); 

    const width = parentWidth;
    const height = parentWidth  * 1.25;

    return (
        <div className="hf-feed-card-media-preview-video"  ref={ref}>
            <MomoizedFeedCardMediaPreviewVideo 
                playing={videoState.playing}
                preload={videoState.preload}
                width={width}
                height={height}
                media={media}
                handleClick={handleClick}
            />
        </div>
    )
}

export default React.memo(FeedCardMediaPreviewVideoHOC, (prevProps, nextProps) => {
    return prevProps?.media?._id === nextProps?.media?._id &&
           prevProps.parentWidth === nextProps.parentWidth &&
           prevProps.modalIsOpen === nextProps.modalIsOpen;
});